import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnDestroy,
	OnInit
} from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, RouterModule } from '@angular/router';
import { AppDataService } from '@go-modules/services/app-data/app-data.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { ThemeEngine } from 'lti/theme';
import { Subject, filter, takeUntil } from 'rxjs';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { EventService, GoEvent } from '@go-modules/services/event/event.service';
import { NgxFullstoryService } from '@go-modules/services/fullstory/fullstory.service';
import { FULLSTORY_EVENTS } from 'go-modules/services/fullstory/fullstory.events';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { GoBannerService, goBannerServiceToken } from 'go-modules/go-banner/go-banner.service';
import { AnnouncementType } from 'go-modules/models/announcement/announcement.type';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { UserService as NgxUserService } from '@go-modules/services/user/user.service';
import { Group as GroupFactory, groupToken } from 'go-modules/models/group-dep/group.factory';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { NgxUserpilotService } from '@go-modules/services/userpilot/userpilot.service';
import { buildUserpilotIdentifyProperties } from '@go-modules/services/userpilot/userpilot-identify-properties';
import { CommonModule } from '@angular/common';
import { GoToastModule } from '@go-modules/components/go-toast/go-toast.module';
import { UserSessionExpirationModule } from '../user-session-expiration';
import { IframeBannerModule } from '../iframe-banner';
import { DashboardModule } from '@go-modules/components/dashboard/dashboard.module';

@Component({
	selector: 'lti-root-view',
	templateUrl: './lti-root-view.component.html',
	styleUrls: ['./lti-root-view.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		GoToastModule,
		UserSessionExpirationModule,
		RouterModule,
		IframeBannerModule,
		DashboardModule
	]
})
export class LtiRootViewComponent implements OnInit, OnDestroy {
	public isIframe: boolean = false;
	public isBannerOpen: boolean = false;
	public sessionEnded: boolean = false;

	private destroy$ = new Subject<void>();
	private initialized: boolean = false;
	private wasInSessionRoute: boolean = false;

	constructor (
		private appDataService: AppDataService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		private router: Router,
		private route: ActivatedRoute,
		private eventService: EventService,
		private fullstoryService: NgxFullstoryService,
		private featureFlag: NgxFeatureFlagService,
		@Inject(goBannerServiceToken) private goBannerService: GoBannerService,
		private translate: TranslateService,
		private ngxUserService: NgxUserService,
		private activityService: NgxActivityService,
		private userpilotService: NgxUserpilotService,
		@Inject(groupToken) private groupFactory: ReturnType<typeof GroupFactory>,
		@Inject('Window') private window: Window
	) {}

	public ngOnInit (): void {
		this.appDataService.appData$
			.pipe(
				takeUntil(this.destroy$),
				filter((data) => !!data)
			)
			.subscribe((appData) => {
				if (!this.initialized) {
					this.initialize(appData);
					this.initialized = true;
				}
			});

		this.eventService.events
			.pipe(
				takeUntil(this.destroy$),
				filter((event: GoEvent) => [
					EVENT_NAMES.AUTH_ENDED
				].includes(event.name as EVENT_NAMES))
			)
			.subscribe((event) => this.handleEvent(event));

		this.router.events
			.pipe(
				takeUntil(this.destroy$),
				filter((event) => event instanceof NavigationEnd)
			)
			.subscribe((event: NavigationEnd) => {
				void this.userpilotService.reload();
				this.handleNavigation(event.urlAfterRedirects);
			});
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public onBannerClosed (): void {
		this.isBannerOpen = false;
	}

	private initialize (appData: any): void {
		const user = appData.currentUser;
		const group = appData.userGroup;
		const activity = appData.activity;

		this.selectedService.setMyOrgs(appData.myOrgs ?? []);
		this.selectedService.setMentoredUserIds(appData.mentoredUserIds ?? null);

		const org = appData.myOrgs?.find((item) => parseInt(item.group_id, 10) === parseInt(appData.org_id, 10));
		if (org) {
			this.selectedService.setOrg(this.groupFactory.model(org));
		}

		if (appData.myAccount) {
			this.selectedService.setAccount(appData.myAccount);
		}

		this.selectedService.setGroup(group, activity);
		this.selectedService.setActivity(activity);

		this.isIframe = this.window.self !== this.window.top;
		this.isBannerOpen = this.isIframe;
		void this.userpilotService.identify(user.user_id, buildUserpilotIdentifyProperties({
			surface: 'lti',
			user,
			org,
			lti: {
				lmsName: appData.lmsName,
				version: appData.lti_version,
				isCourseLevel: appData.hasCourseLevelLTI,
				readonly: appData.readonly === true,
				groupRole: group?.role
			}
		}));

		this.getAllActivityStats(activity).then(() => {
			this.selectedService.setGroups([group]);
			this.setPageTitle(appData, activity);
			this.identifyFullstoryEvents(appData, group);
			this.handleWarnings(appData);
			this.loadBillingBalances(user);
		});
	}

	private async getAllActivityStats (activity): Promise<void> {
		if (!activity) {
			return;
		}

		await this.activityService.getStats(activity.group_id).toPromise();
	}

	private setPageTitle (appData, activity): void {
		ThemeEngine.setPageTitle(ThemeEngine.getTheme(appData), activity);
	}

	private identifyFullstoryEvents (appData, group): void {
		if (!group) {
			return;
		}

		this.fullstoryService.createEvent(FULLSTORY_EVENTS.USER_CONTEXT_IDENTIFIED, {
			userRole_str: group.role,
			groupId_int: parseInt(group.group_id, 10),
			orgId_int: parseInt(group.org_id, 10)
		});

		if (!group.hasInstructorRole?.(true)) {
			return;
		}

		this.fullstoryService.createEvent(FULLSTORY_EVENTS.USE_TYPE_IDENTIFIED, {
			useTypeId_int: parseInt(appData.currentUser?.use_type_id, 10)
		});
	}

	private handleWarnings (appData): void {
		if (!this.featureFlag.isAvailable('WARN_LTI_USERS_UPDATE_CONFIG') || !appData.warning_translation) {
			return;
		}

		const { warning_translation: warning } = appData;
		const daysRemaining = warning.daysRemaining;

		if (daysRemaining < 0) {
			const knownIssues = Object.keys(warning.issues ?? {}).filter((key) => warning.issues[key]).join(', ');
			this.translate.get('lti-warning_deprecated', { issues: knownIssues })
				.subscribe((message) => this.showWarningBanner(message, AnnouncementType.WARNING));
			return;
		}

		const translateParams = {
			date: dayjs(warning.date).format('MMM D, YYYY'),
			daysRemaining
		};

		// here to avoid unused translation warnings
		// lti-warning_valid-domain-expiring-oauth
		// lti-warning_valid-domain-with-issues
		// lti-warning_wrong-domain
		// lti-warning_wrong-domain-with-issues
		this.translate.get(warning.translate_key, translateParams)
			.subscribe((message) => {
				const type = daysRemaining <= 14 ? AnnouncementType.WARNING : AnnouncementType.INFO;
				this.showWarningBanner(message, type);
			});
	}

	private showWarningBanner (message: string, type: AnnouncementType): void {
		this.fullstoryService.createEvent(FULLSTORY_EVENTS.LTI_WARNING, { message });
		this.goBannerService.addBanner({
			content: message,
			name: 'lti-warning',
			isDismissable: type !== AnnouncementType.WARNING,
			maxViews: 10000,
			type
		});
	}

	private loadBillingBalances (user: any): void {
		if (!user?.user_id) {
			return;
		}

		this.ngxUserService.getBillingBalances(user.user_id).subscribe((response) => {
			user.plan = response.plan;
			user.totalStudentCredits = response.credits.reduce((total, credit) => {
				return total + Number(credit.units_available);
			}, 0);
		});
	}

	private handleEvent (event: GoEvent): void {
		switch (event.name) {
			case EVENT_NAMES.AUTH_ENDED:
				this.userpilotService.reset();
				this.sessionEnded = true;
				break;
		}
	}

	private handleNavigation (url: string): void {
		const isSessionRoute = url.includes('/dashboard/sessions/');
		if (this.wasInSessionRoute && !isSessionRoute) {
			this.window.sessionStorage.removeItem('currentFilter');
		}

		this.wasInSessionRoute = isSessionRoute;
	}
}
